import { useState, useRef, useEffect, useId } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { getServices } from '@/data/servicesData'
import { useTranslation } from '@/i18n'

/**
 * Hover-opened services menu.
 *
 * Deliberately not built on ui/dropdown-menu: Radix's modal behaviour locks body
 * scroll and adds scrollbar-compensating padding (which shifts the page sideways),
 * and it restores focus to the trigger on close (which flashes a focus ring).
 */
export function ServicesDropdown() {
  const { t, language } = useTranslation()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | undefined>(undefined)
  const triggerRef = useRef<HTMLAnchorElement>(null)
  const panelId = useId()

  const services = getServices(language)

  const cancelClose = () => window.clearTimeout(closeTimer.current)

  const openNow = () => {
    cancelClose()
    setOpen(true)
  }

  // Small grace period so diagonal mouse travel toward the panel doesn't close it.
  const closeSoon = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), 120)
  }

  useEffect(() => cancelClose, [])

  // Close when navigating to one of the items.
  useEffect(() => {
    cancelClose()
    setOpen(false)
  }, [pathname])

  const itemClass =
    'flex items-center gap-3 w-full rounded-md px-2.5 py-2 hover:bg-accent gentle-animation'

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      onFocus={openNow}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
      onKeyDown={e => {
        if (e.key !== 'Escape' || !open) return
        // Escape must also return focus to the trigger, otherwise the panel
        // closes out from under a keyboard user and focus is left on a detached
        // node — the browser then restarts tabbing from the top of the document.
        e.stopPropagation()
        setOpen(false)
        triggerRef.current?.focus()
      }}
    >
      {/* aria-expanded and aria-controls, but deliberately NOT aria-haspopup:
          that announces a menu, and this panel is a group of links with no
          menu semantics, no roving tabindex and no arrow-key model. Promising
          a menu and delivering a link list is worse than promising nothing —
          a screen-reader user would wait for keys that do nothing. */}
      <Link
        ref={triggerRef}
        to="/services"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={`flex items-center gap-1 font-medium gentle-animation text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm ${
          open ? 'text-primary-text' : 'text-foreground/80 hover:text-primary-text'
        }`}
      >
        {t.nav.services}
        <ChevronDown
          className={`w-3.5 h-3.5 gentle-animation ${open ? 'rotate-180' : ''}`}
        />
      </Link>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
            /* pt-3 is an invisible bridge so the pointer can cross the gap. */
            className="absolute left-0 top-full pt-3 z-50"
          >
            <div
              id={panelId}
              className="w-72 bg-popover border border-border rounded-lg shadow-lg p-1.5"
            >
              {services.map(service => {
                const Icon = service.icon
                return (
                  <Link key={service.id} to={`/services/${service.id}`} className={itemClass}>
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{service.title}</span>
                  </Link>
                )
              })}
              {/* No "All services" item: the Services trigger itself opens
                  the Services page (14 October 2026). */}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
